import React, { useContext, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  CardActions,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "../Utility/UserMessageContext";
import { searchPatients } from "../API/PatientsApi";
import authContext from "../Context/AuthProvider";
import { SearchRounded, VisibilityOutlined } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { formatAge } from "../Utility/Utility";

const PatientSearchDialog = ({
  showSearchPanel,
  setShowSearchPanel,
  onViewDetails,
}) => {
  const { auth } = useContext(authContext);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const searchResultColumns = [
    { field: "mrn", headerName: "MRN", width: 100 },
    { field: "firstName", headerName: "First Name", width: 150 },
    { field: "lastName", headerName: "Last Name", width: 150 },
    { field: "gender", headerName: "Gender", width: 100 },
    {
      field: "ageInYears",
      headerName: "Age",
      width: 80,
      renderCell: (params) => formatAge(params?.row?.ageInYears),
    },

    { field: "mobileNo", headerName: "Mobile No.", width: 150 },
    { field: "emailId", headerName: "Email Id", width: 200 },
  ];

  const onPatientSelected = () => {
    const selectedPatient = searchResult.find(
      (patient) => patient.id === selectedPatientId
    );
    if (selectedPatient) {
      onViewDetails(selectedPatient);
    }
    setShowSearchPanel(false);
  };

  const searchPatient = async (e) => {
    e.preventDefault();
    setIsSearchClicked(true); // Set search clicked to true
    setIsLoading(true); // Set loading state to true
    setSearchResult([]); // Clear previous results while loading

    if (searchKeyword.trim().length < 3) {
      showSnackbar(
        "Enter at least 3 characters to search. Special characters are not allowed.",
        "error"
      );
      setIsLoading(false); // Reset loading state
    } else {
      try {
        const result = await searchPatients(auth, searchKeyword);
        if (result?.isSuccess) {
          setSelectedPatientId(null);
          setSearchResult(result?.data || []); // Ensure searchResult is an array
        } else {
          setSearchResult([]); // Set to empty array if no results
        }
      } catch (error) {
        console.error("Error searching patients:", error);
        setSearchResult([]); // Set to empty array on error
      } finally {
        setIsLoading(false); // Reset loading state
      }
    }
  };

  const resetDialog = () => {
    setShowSearchPanel(false);
    setIsSearchClicked(false);
    setSearchResult([]);
    setSelectedPatientId(null);
    setIsLoading(false);
  };

  const renderCardView = () => {
    return (
      <Box sx={{ mt: 2, px: 1, overflowY: "auto", flex: 1 }}>
        {isLoading ? (
          <Typography variant="body1" align="center" py={3}>
            Loading...
          </Typography>
        ) : searchResult.length === 0 ? (
          <Typography variant="body1" align="center" py={3}>
            {isSearchClicked && searchKeyword.trim()
              ? "There is no existing data of this type."
              : "No rows"}
          </Typography>
        ) : (
          searchResult.map((patient) => (
            <Card
              key={patient.id}
              variant="outlined"
              sx={{
                mb: 2,
                borderRadius: 2,
                border:
                  selectedPatientId === patient.id
                    ? `2px solid ${theme.palette.secondary.main}`
                    : undefined,
                bgcolor:
                  selectedPatientId === patient.id ? "#f0f0f0" : undefined,
              }}
              onClick={() => setSelectedPatientId(patient.id)}
            >
              <CardContent sx={{ padding: 2 }}>
                <Typography variant="body1" fontWeight="bold">
                  {`${patient.firstName || ""} ${patient.lastName || ""}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  MRN: {patient.mrn || "-"}
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ "& > div": { mb: 1 } }}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2">Gender:</Typography>
                    <Typography variant="body2">
                      {patient.gender || "-"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2">Age:</Typography>
                    <Typography variant="body2">
                      {formatAge(patient.ageInYears)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2">Mobile:</Typography>
                    <Typography variant="body2">
                      {patient.mobileNo || "-"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2">Email:</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: "60%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {patient.emailId || "-"}
                    </Typography>
                  </Box>
                </Box>

                <CardActions sx={{ justifyContent: "flex-end", p: 0, mt: 1 }}>
                  {/* <Button
                                        size="small"
                                        variant="contained"
                                        color="secondary"
                                        startIcon={<VisibilityOutlined />}
                                        onClick={() => {
                                            setSelectedPatientId(patient.id);
                                            onPatientSelected();
                                        }}
                                    >
                                        Select
                                    </Button> */}
                </CardActions>
              </CardContent>
            </Card>
          ))
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={showSearchPanel}
      onClose={() => {
        setShowSearchPanel(false);
        setIsSearchClicked(false); // Reset search click state
        setSearchResult([]); // Clear search results
        setSelectedPatientId(null); // Clear selected patient
        setIsLoading(false); // Reset loading state
      }}
      maxWidth={isMobile ? "sm" : "md"}
      fullWidth
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      PaperProps={{
        sx: {
          width: isMobile ? "90%" : "100%",
          maxHeight: isMobile ? "80vh" : "60vh",
          height: isMobile ? "80vh" : "60vh",
        },
      }}
    >
      <DialogTitle>Search Patient</DialogTitle>
      <DialogContent
        dividers
        sx={{ overflowY: "hidden", display: "flex", flexDirection: "column" }}
      >
        <Grid2 container size={12}>
          <Grid2 size={2}></Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
            <form onSubmit={searchPatient} noValidate>
              <TextField
                label={
                  isMobile
                    ? "Search patient"
                    : "Search patient by name, phone number, email id, or MRN"
                }
                placeholder={
                  isMobile
                    ? "Name, phone, email, MRN"
                    : "Search patient by name, phone number, email id, or MRN"
                }
                variant="outlined"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={searchPatient}
                      edge="end"
                      size={isMobile ? "small" : "medium"}
                    >
                      <SearchRounded />
                    </IconButton>
                  ),
                }}
                sx={{ width: "100%", mb: 3, mt: 1 }}
                size={isMobile ? "small" : "medium"}
              />
            </form>
          </Grid2>
        </Grid2>

        <Typography variant={isMobile ? "body2" : "h6"}>
          Search Result
        </Typography>

        {isMobile ? (
          renderCardView()
        ) : (
          <Box sx={{ height: 250, width: "100%" }}>
            <DataGrid
              rows={searchResult}
              density="compact"
              columns={searchResultColumns}
              getRowId={(row) => row.id}
              disableColumnMenu
              onRowClick={(params) => setSelectedPatientId(params.id)}
              hideFooter
              loading={isLoading}
              slotProps={{
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
              rowSelectionModel={selectedPatientId ? [selectedPatientId] : []}
              localeText={{
                noRowsLabel: isLoading
                  ? "Loading..."
                  : isSearchClicked &&
                    searchKeyword.trim() &&
                    searchResult.length === 0
                  ? "There is no existing data of this type."
                  : "No rows",
              }}
              sx={{
                "& .MuiDataGrid-row.Mui-selected": {
                  bgcolor: "#f0f0f0 !important",
                },
                "& .MuiDataGrid-cell": {
                  fontSize: "0.875rem",
                  padding: "8px",
                },
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "#e0e0e0",
                },
                "& .MuiTablePagination-toolbar": {
                  minHeight: "unset",
                  alignItems: "center",
                  paddingTop: "0 !important",
                  paddingBottom: "0 !important",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  {
                    margin: 0,
                  },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: isMobile ? 2 : 1,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 1 : 0,
        }}
      >
        {isMobile ? (
          <>
            <Button
              variant="contained"
              color="secondary"
              disabled={!selectedPatientId}
              onClick={onPatientSelected}
              fullWidth
            >
              Select Patient
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              onClick={resetDialog}
              fullWidth
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => {
                setShowSearchPanel(false);
                setIsSearchClicked(false);
                setSearchResult([]);
                setSelectedPatientId(null);
                setIsLoading(false); // Reset loading state
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={!selectedPatientId}
              onClick={onPatientSelected}
            >
              Select Patient
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PatientSearchDialog;
